import { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Trophy, Send, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CoachChallengeWidget = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchWidget = async () => {
    try {
      const res = await axios.get(`${API}/challenges/coach/widget`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchWidget(); }, []);

  const remind = async (athleteId) => {
    try {
      await axios.post(`${API}/challenges/coach/remind?athlete_id=${athleteId}`, {}, { headers: getAuthHeaders() });
      toast.success('Reminder sent');
    } catch (e) { toast.error('Failed to send reminder'); }
  };

  if (loading || !data) return null;
  if (!data.challenge) return null;

  const { challenge, team_size, completed, completion_rate, top_performers, missing } = data;

  return (
    <div className="bg-gradient-to-br from-primary/5 via-card to-black border border-primary/30 rounded-sm p-5" data-testid="coach-challenge-widget">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="text-primary" size={22} />
          <div>
            <h3 className="font-heading text-lg uppercase tracking-tight">Weekly Challenge</h3>
            <p className="text-xs text-muted-foreground">{challenge.title}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-heading text-3xl text-primary">{completed}<span className="text-muted-foreground text-sm">/{team_size}</span></p>
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{completion_rate}% participation</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-muted/30 rounded-full overflow-hidden mb-4">
        <div className="h-full bg-gradient-to-r from-primary to-amber-300 transition-all" style={{ width: `${completion_rate}%` }} />
      </div>

      {/* Top performers */}
      {top_performers.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
            <Users size={11} /> Top from your team
          </p>
          <div className="space-y-1.5">
            {top_performers.slice(0, 3).map((p) => (
              <div key={p.athlete_id} className="flex items-center justify-between text-sm">
                <span className="truncate">{p.rank <= 3 ? ['🥇','🥈','🥉'][p.rank - 1] : `#${p.rank}`} {p.athlete_name}</span>
                <span className="font-heading text-primary">{p.best_score}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Missing */}
      {missing.length > 0 && (
        <div className="mb-4">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Haven't participated yet ({missing.length})</p>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {missing.slice(0, 5).map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm bg-muted/20 rounded-sm px-2 py-1">
                <span className="truncate">{m.name}</span>
                <Button size="sm" variant="ghost" className="h-7 text-xs text-primary hover:bg-primary/10" onClick={() => remind(m.id)} data-testid={`remind-${m.id}`}>
                  <Send size={11} className="mr-1" /> Remind
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      <Link to="/coach/weekly-challenge" className="text-xs uppercase tracking-widest text-primary hover:underline">View full challenge →</Link>
    </div>
  );
};

export default CoachChallengeWidget;
